import { checkAuthzActions } from '$lib/api/sdk.gen';
import type { SecurityScope } from '$lib/api/security-scopes.gen';
import { ResourceType, type AuthzCheckResource } from '$lib/api/types.gen';

export type { SecurityScope };

const TTL_MS = 60 * 60 * 1000;

const cache = new Map<string, { expiresAt: number; promise: Promise<boolean> }>();

function cacheKey(permission: string, resourceType: ResourceType, resourceId: string): string {
	return `${permission}:${resourceType}:${resourceId}`;
}

function getCachedAuthzAction(
	permission: string,
	resourceType: ResourceType,
	resourceId: string,
	now: number,
): Promise<boolean> | undefined {
	const cached = cache.get(cacheKey(permission, resourceType, resourceId));
	if (cached && cached.expiresAt > now) return cached.promise;
	return undefined;
}

function normalizeDefaultAllowed(
	defaultAllowed: boolean | boolean[],
	permissionsCount: number,
): boolean[] {
	return Array.isArray(defaultAllowed)
		? defaultAllowed
		: Array.from({ length: permissionsCount }, () => defaultAllowed);
}

/**
 * Permission checks for a single resource instance.
 */
export type ResourceChecks = {
	resource: AuthzCheckResource;
} & (
	| {
			check: SecurityScope;
	  }
	| {
			checks: SecurityScope[];
	  }
);

/**
 * One or more resource-specific permission check groups.
 */
export type Checks = ResourceChecks | ResourceChecks[];

/**
 * Resolves the requested permission checks in input order.
 *
 * Returns one boolean per requested permission. If a fallback is configured,
 * that value is used when the authz check cannot be completed.
 */
export function loadAuthzActions(
	checks: Checks,
	options?: { defaultAllowed?: boolean | boolean[]; warnMessage?: string },
): Promise<boolean[]> {
	const checksList = Array.isArray(checks) ? checks : [checks];
	const requestedChecks = checksList.flatMap((checkGroup) => {
		const permissions = 'checks' in checkGroup ? checkGroup.checks : [checkGroup.check];
		return permissions.map((permission) => ({
			permission,
			resourceType: checkGroup.resource.type,
			resourceId: checkGroup.resource.id,
		}));
	});
	const now = Date.now();
	const cachedPromises = requestedChecks.map(({ permission, resourceType, resourceId }) =>
		getCachedAuthzAction(permission, resourceType, resourceId, now),
	);
	if (cachedPromises.every((promise) => promise !== undefined)) {
		return Promise.all(cachedPromises);
	}

	const defaults = normalizeDefaultAllowed(
		options?.defaultAllowed ?? false,
		requestedChecks.length,
	);

	const sharedPromise = checkAuthzActions({
		body: {
			checks: checksList.map((checkGroup) => ({
				permissions: 'checks' in checkGroup ? [...checkGroup.checks] : [checkGroup.check],
				resource: checkGroup.resource,
			})),
		},
		throwOnError: false,
	})
		.then((res) => {
			if ('error' in res && res.error) throw res.error;
			return checksList.flatMap((checkGroup, groupIndex) =>
				('checks' in checkGroup ? checkGroup.checks : [checkGroup.check]).map(
					(_permission: SecurityScope, checkIndex: number) =>
						res.data.results[groupIndex]?.allowed[checkIndex] === true,
				),
			);
		})
		.catch((e) => {
			console.warn(options?.warnMessage ?? '[authz] permission check failed', e);
			for (const { permission, resourceType, resourceId } of requestedChecks) {
				cache.delete(cacheKey(permission, resourceType, resourceId));
			}
			return defaults;
		});

	for (const [index, { permission, resourceType, resourceId }] of requestedChecks.entries()) {
		cache.set(cacheKey(permission, resourceType, resourceId), {
			expiresAt: now + TTL_MS,
			promise: sharedPromise.then((allowed) => allowed[index] ?? defaults[index] ?? false),
		});
	}

	return sharedPromise;
}

function invalidateAuthzAction(
	permissions: readonly string[],
	resourceType: ResourceType,
	resourceId?: string,
): void {
	if (resourceId === undefined) {
		for (const permission of permissions) {
			const prefix = `${permission}:${resourceType}:`;
			for (const key of cache.keys()) {
				if (key.startsWith(prefix)) cache.delete(key);
			}
		}
		return;
	}

	for (const permission of permissions) {
		cache.delete(cacheKey(permission, resourceType, resourceId));
	}
}

/**
 * Resolves whether the current user is allowed to update the given project.
 *
 * If the authz check cannot be completed, this defaults to allowed.
 */
export function loadProjectEditable(projectId: string): Promise<boolean> {
	return loadAuthzActions(
		{
			resource: { type: ResourceType.ORG_PROJECT, id: projectId },
			checks: ['org.project:update'],
		},
		{
			defaultAllowed: true,
			warnMessage: '[authz] org.project:update check failed; defaulting to editable',
		},
	).then(([allowed]) => allowed);
}

export function loadCanUpdateProjectMember(projectId: string): Promise<boolean> {
	return loadAuthzActions(
		{
			resource: { type: ResourceType.ORG_PROJECT, id: projectId },
			checks: ['org.project:update_member'],
		},
		{
			warnMessage: '[authz] org.project:update_member check failed; hiding role editor',
		},
	).then(([allowed]) => allowed);
}
